<template>
  <SrpModal v-model:isVisible="_isVisible" size="small" :isClosable="!(_username && userProfileStore.getViewingUserProfile.username !== _username)">
    <template #content>
      <h1 class="global-h1" style="margin-bottom: 15px">Customize Username</h1>

      <input
        :class="{
          'global-text-input': true,
          'global-text-input--error': errorMessage,
        }"
        type="text"
        :value="_username"
        placeholder="New Username"
        style="max-width: 300px; margin-bottom: 20px"
        @input="$event => (_username = $event.target.value)"
        @focus="errorMessage = ''"
      />

      <div v-if="errorMessage" class="global-error-string" style="margin-bottom: 20px">
        {{ errorMessage }}
      </div>

      <ul style="gap: 7px; padding: 0; margin: 0 0 20px; display: flex; flex-direction: column; list-style: none">
        <li>
          <span>
            <b>Username Requirements</b><br />
            • Must be between 4 and 30 characters<br />
            • Must be unique<br />
            • Can use letters, numbers, periods, and underscores
          </span>
        </li>
      </ul>
    </template>

    <template #footer>
      <SrpButton fill="outlined" color="gray" style="margin-right: 12px" @click="_isVisible = false">Cancel</SrpButton>

      <SrpButton @click="save" :isDisabled="isSaving || _username === userProfileStore.getViewingUserProfile.username">
        <template v-if="isSaving" #icon><LoadingIcon :size="18" /></template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import UserProfileRepo from "@repos/UserProfleRepo";

// Types
import { UsernameValidationResult } from "@contracts/userProfile";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";
const userProfileStore = useUserProfileStore();

const emit = defineEmits<{
  (e: "save", newUsername: string): void;
}>();

// Toggle visibility ==========================================================
const _isVisible = ref<boolean>(false);

// Username input value =======================================================
const _username = ref<string>("");

// set local username value when the modal opens
watch(_isVisible, () => {
  if (_isVisible.value) {
    _username.value = userProfileStore.getViewingUserProfile.username;
  }
});

// Save =======================================================================
const isSaving = ref<boolean>(false);
const errorMessage = ref<string>("");

async function save() {
  isSaving.value = true;

  const usernameValidationResult: UsernameValidationResult = await UserProfileRepo.validateUsername(userProfileStore.getViewingUserProfile.authNameId, _username.value);
  errorMessage.value = usernameValidationResult.errorMessage;

  if (usernameValidationResult.isValid === false) {
    isSaving.value = false;
    return;
  }

  await UserProfileRepo.updateUserName(userProfileStore.getViewingUserProfile.authNameId, _username.value);
  userProfileStore.setUserNameLocally(_username.value);

  isSaving.value = false;
  emit("save", _username.value);
  _isVisible.value = false;
}

// Define expose ==============================================================
defineExpose({
  openModal: () => (_isVisible.value = true),
});
</script>

<style scoped lang="scss"></style>
